import React, { useState } from 'react'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Modal, Table, Tag, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { deleteGroupUser, fetchGroupSingleUsers, fetchGroupUserList, modifyGroup } from '../../apis/group'
import { deleteUser, fetchUser, modifyUser } from '../../apis/user'
import MainTable from '../../components/Basic/MainTable'
import { UserBrandTable } from './group.styled'
import PopupConfirm from '../../components/Basic/PopupConfirm'
import { deleteBrandUser, editBrandUser, quitAllBrands } from '../../apis/brand'
import AddUserModal from './modals/AddUserModal'
import EditUserModal from './modals/EditUserModal'
import AddBrandToUserModal from './modals/AddBrandToUserModal'

function AdminUserTable (props) {
  const { params, brandsOption } = props
  const { t: ln_t } = useTranslation()
  const { fetchParams, handleFetchParams } = params
  const [userGroupList, setUserGroupList] = useState({})
  const [userBrandList, setUserBrandList] = useState({})

  const [addGroupUser, setAddGroupUser] = useState(false)
  const [editGroupUser, setEditGroupUser] = useState(false)
  const [addBrandUser, setAddBrandUser] = useState(false)
  const [modalData, setModalData] = useState({})

  const apiFunc = async () => fetchGroupUserList({ ...fetchParams, role: 'member' })
  const getUserBrandList = async (userId, clickedGroupId) => {
    const result = await fetchUser(userId, { includes: 'brands' })
    if (result?.status === 200) {
      const allBrands = result?.data?.brands.filter(i => i?.brandUser?.isDelete === 'no')
      allBrands.forEach(brand => Object.assign(brand, { clickedGroupId }))
      setUserBrandList(prev => ({ ...prev, [userId]: allBrands }))
      return allBrands
    }
  }
  const getGroupUserDetails = async (id) => {
    const result = await fetchGroupSingleUsers(id)
    if (result?.status === 200) {
      const { roles, groups } = result?.data?.[0] || {}
      const roleObj = roles?.reduce((all, val) => ({ ...all || {}, [val?.groupId]: [...all[val?.groupId] || [], val] }), {})
      const group = groups?.map(i => ({ ...i, userId: id, groupUser: { ...i[i.groupUser] || {}, roles: roleObj[i.id] } }))
      setUserGroupList(prev => ({ ...prev, [id]: group }))
      return group
    }
  }

  // ----- user -----
  const handleChangeUserStatus = async (record, updates) => {
    const res = await modifyUser(record?.id, updates)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      handleFetchParams('add', {})
    }
  }
  const handleDeleteUser = ({ id, name }) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${ln_t('user.user')}`,
      content: `${ln_t('message.confirm.delete')} ${name}?`,
      onOk: async () => {
        const res = await deleteUser(id)
        if (res?.status === 204) {
          message.success(ln_t('message.success.remove'))
          handleFetchParams('add', {})
        }
      }
    })
  }

  // ----- group -----
  const handleChangeGroupStatus = async (record, updates) => {
    const res = await modifyGroup(record?.id, updates)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      getGroupUserDetails(record?.userId)
    }
  }
  const handleAddUserToGroup = async record => {
    const groups = userGroupList[record?.id] || await getGroupUserDetails(record.id)
    setModalData({ ...record, groups })
    setAddGroupUser(true)
  }
  const handleEditGroupUserRole = async ({ groupUser, ...other }) => {
    setEditGroupUser(true)
    setModalData({ ...other, roles: groupUser?.roles })
  }
  const handleDeleteUserFromGroup = ({ id, userId }) => {
    Modal.confirm({
      title: `${ln_t('user.removeUser')}`,
      content: `${ln_t('user.removeUserFromThisGroup')}?`,
      onOk: async () => {
        const quitBrand = await quitAllBrands(id, userId)
        const quitGroup = await deleteGroupUser(id, userId)
        if (quitBrand?.status === 200 && quitGroup.status === 204) {
          message.success(ln_t('message.success.remove'))
          getGroupUserDetails(userId)
        }
      }
    })
  }

  // ----- brand -----
  const handleChangeUserBrand = async (record, updateParams) => {
    const { brandId, userId } = record?.brandUser || {}
    const res = await editBrandUser(brandId, userId, updateParams)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      getUserBrandList(userId)
    }
  }
  const handleDeleteUserBrand = async record => {
    const { brandUser, name } = record || {}
    const { brandId, userId } = brandUser || {}
    Modal.confirm({
      title: `${ln_t('user.removeBrand')}`,
      content: `${ln_t('user.removeBrandMessage')} ${name}?`,
      onOk: async () => {
        const result = await deleteBrandUser(brandId, userId)
        if (result?.status === 204) {
          message.success(ln_t('message.success.remove'))
          getUserBrandList(userId)
        }
      }
    })
  }
  const handleAddUserToBrand = async (record) => {
    const brands = await getUserBrandList(record?.userId)
    setModalData({ ...record, brands })
    setAddBrandUser(true)
  }

  // ----- table -----
  const formatDate = date => dayjs(date).format('YYYY-MM-DD')
  const userColumns = [
    { title: ln_t('group.name'), dataIndex: 'name' },
    { title: ln_t('user.email'), dataIndex: 'email', sorter: true },
    {
      title: ln_t('group.status'),
      dataIndex: 'status',
      render: (e, record) => <PopupConfirm type="status" checked={e} onConfirm={() => handleChangeUserStatus(record, { status: e ? 0 : 1 })} />
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', render: e => formatDate(e) },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', render: e => formatDate(e) },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, record) => <div className="action-btn">
          <Tooltip title={ln_t('user.addToGroup')}>
            <Button size="small" icon={<PlusOutlined />} onClick={() => handleAddUserToGroup(record)} />
          </Tooltip>
          <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteUser(record)} />
        </div>
    }
  ]

  const groupColumns = [
    { title: ln_t('group.group'), dataIndex: 'name' },
    { title: ln_t('group.role'), dataIndex: 'groupUser', render: e => e?.roles?.map((role) => <Tag key={role?.id}>{role?.name}</Tag>) },
    {
      title: ln_t('group.status'),
      dataIndex: 'status',
      render: (e, record) => <PopupConfirm type="status" checked={e} onConfirm={() => handleChangeGroupStatus(record, { status: e ? 0 : 1 })} />
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', render: e => formatDate(e) },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', render: e => formatDate(e) },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, record) => <div className="action-btn">
        <Tooltip title={ln_t('user.addToBrand')}>
          <Button size="small" icon={<PlusOutlined />} onClick={() => handleAddUserToBrand(record)} />
        </Tooltip>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEditGroupUserRole(record)} />
        <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteUserFromGroup(record)} />
      </div>
    }
  ]

  const brandColumns = [
    { title: ln_t('group.brand'), dataIndex: 'name' },
    // {
    //   title: `${ln_t('user.brand')} ${ln_t('user.role')}`,
    //   dataIndex: 'brandUser',
    //   render: (e, record) => <PopupConfirm type="role" checked={e.role === 'admin'} onConfirm={() => handleChangeUserBrand(record, { role: e.role === 'admin' ? 'member' : 'admin' })} />
    // },
    {
      title: `${ln_t('user.brand')} ${ln_t('util.status')}`,
      dataIndex: 'brandUser',
      render: (e, record) => <PopupConfirm type="status" checked={e.status} onConfirm={() => handleChangeUserBrand(record, { status: e.status ? 0 : 1 })} />
    },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, record) => <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteUserBrand(record)}>{ln_t('user.removeBrand')}</Button>
    }
  ]

  const tableProps = { sortDirections: ['ascend', 'descend', 'ascend'], rowKey: record => record.id, pagination: false, scroll: { x: 'true' }, showSorterTooltip: false }
  const expandable = {
    onExpand: async (expanded, { id }) => { getGroupUserDetails(id) },
    expandedRowRender: (record) => <UserBrandTable>
      <Table
        columns={groupColumns}
        dataSource={userGroupList[record.id] || []}
        {...tableProps}
        expandable={{
          onExpand: async (expanded, { userId }) => getUserBrandList(userId),
          expandedRowRender: ({ userId, id }) => <UserBrandTable>
            <Table {...tableProps} columns={brandColumns} dataSource={userBrandList?.[userId]?.filter(i => i.groupId === id) || []}/>
          </UserBrandTable>
        }}
      />
    </UserBrandTable>
  }

  // ----- callback -----
  const addUserGroupSuccessCallback = userId => {
    message.success(ln_t('message.success.add'))
    getGroupUserDetails(userId)
  }
  const editGroupUserSuccessCallback = userId => {
    message.success(ln_t('message.success.modify'))
    getGroupUserDetails(userId)
  }
  const addBrandUserSuccessCallback = userId => {
    message.success(ln_t('message.success.add'))
    getUserBrandList(userId)
  }

  return (
    <>
      <MainTable
        columns={userColumns}
        rowKey="id"
        apiFunc={apiFunc}
        params={params}
        expandable={expandable}
      />
      <AddUserModal
        adminMode
        groupMode
        {...props}
        show={addGroupUser}
        setShow={setAddGroupUser}
        successCallback={addUserGroupSuccessCallback}
        data={modalData}
      />
      <EditUserModal
        adminMode
        show={editGroupUser}
        setShow={setEditGroupUser}
        data={modalData}
        successCallback={editGroupUserSuccessCallback}
      />
      <AddBrandToUserModal
        {...props}
        adminMode
        show={addBrandUser}
        setShow={setAddBrandUser}
        data={modalData}
        successCallback={addBrandUserSuccessCallback}
        brandOptions={brandsOption}
      />
    </>
  )
}

export default AdminUserTable
